/**
 * @fileoverview Config do descadastro automático (opt-out) por empresa. Quando o
 * cliente responde a um template — clicando numa quick reply tipo "Bloquear
 * mensagens" ou digitando uma palavra abaixo — o número entra na blocklist e não
 * recebe novos disparos. Frases-gatilho e a mensagem de confirmação são editáveis
 * aqui. Persiste em /mass-send/optout-config (Settings por empresa).
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const OptOutConfigModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [phrasesText, setPhrasesText] = useState("");
  const [reply, setReply] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    api
      .get("/mass-send/optout-config")
      .then(({ data }) => {
        if (!active) return;
        setEnabled(data.enabled !== false);
        setPhrasesText((data.phrases || []).join("\n"));
        setReply(data.reply || "");
      })
      .catch((e) => {
        if (active) toastError(e);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const phrases = phrasesText
        .split("\n")
        .map((p) => p.trim())
        .filter(Boolean);
      await api.put("/mass-send/optout-config", { enabled, phrases, reply });
      toast.success("Configuração de descadastro salva.");
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Descadastro automático</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
              Quando o cliente responde a um <strong>disparo em massa</strong> — clicando
              numa resposta rápida (ex.: "Bloquear mensagens") ou enviando uma das
              palavras abaixo — o número é adicionado à lista de bloqueio e deixa de
              receber novos disparos. Botões de outros fluxos/bots da empresa nunca
              descadastram.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="Ativar descadastro automático"
            />

            <TextField
              label="Palavras/frases que descadastram (uma por linha)"
              value={phrasesText}
              onChange={(e) => setPhrasesText(e.target.value)}
              multiline
              minRows={5}
              fullWidth
              variant="outlined"
              margin="normal"
              disabled={!enabled}
              helperText={
                "O texto do botão ou da mensagem precisa ser EXATAMENTE uma dessas frases " +
                "(maiúsculas e acentos são ignorados). Botão: só em resposta a um disparo " +
                "em massa. Mensagem digitada: só descadastra quem já recebeu um disparo."
              }
            />

            <TextField
              label="Mensagem de confirmação enviada ao cliente"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              variant="outlined"
              margin="normal"
              disabled={!enabled}
              helperText="Enviada uma vez, logo após o descadastro. Deixe em branco para não responder."
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || loading}
        >
          {saving ? "Salvando…" : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OptOutConfigModal;
